/**
 * Analytics Utility
 * 
 * Helper functions for Google Analytics tracking
 */

/**
 * Initialize Google Analytics
 * Note: gtag is already initialized in index.html, this is just a placeholder
 */
export const initGA = (): void => {
  // Google Analytics is already initialized in index.html
  // This function is kept for compatibility with useAnalytics hook
  console.log('📊 Google Analytics initialized');
};

/**
 * Track signup conversion event with delayed navigation
 * @param navigate - React Router navigate function
 * @param destination - Optional destination URL (defaults to '/signup')
 */
export const trackSignupConversion = (
  navigate: (path: string) => void,
  destination: string = '/signup'
): void => {
  try {
    // Send analytics event (non-blocking)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      console.log('📊 Tracking signup conversion event');
      (window as any).gtag('event', 'conversion_event_signup', {
        'event_timeout': 2000,
      });
    }
    
    // Navigate immediately (don't wait for analytics callback)
    // This ensures the link always works, even if analytics is slow/blocked
    navigate(destination);
  } catch (error) {
    console.error('❌ Error tracking signup conversion:', error);
    // Fallback: navigate anyway
    navigate(destination);
  }
};

/**
 * Track custom event
 * @param eventName - Name of the event
 * @param eventParams - Optional event parameters
 */
export const trackCustomEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  try {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      console.log(`📊 Tracking custom event: ${eventName}`, eventParams);
      (window as any).gtag('event', eventName, eventParams);
    } else {
      console.warn('⚠️ Google Analytics not available');
    }
  } catch (error) {
    console.error('❌ Error tracking custom event:', error);
  }
};

/**
 * Track page view
 * @param pagePath - Page path
 * @param pageTitle - Optional page title
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  try {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      console.log(`📊 Tracking page view: ${pagePath}`);
      (window as any).gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
      });
    }
  } catch (error) {
    console.error('❌ Error tracking page view:', error);
  }
};

/**
 * Track button click
 * @param buttonName - Name/label of the button
 * @param location - Location/component where button is clicked
 */
export const trackButtonClick = (
  buttonName: string,
  location?: string
): void => {
  trackCustomEvent('button_click', {
    button_name: buttonName,
    location: location,
  });
};

/**
 * Track signup click (when user clicks signup button/link)
 * This is for initial interest in signing up
 * @param source - Where the signup was initiated (e.g., 'pricing_page', 'signin_page', 'hero_section')
 */
export const trackSignupClick = (source?: string): void => {
  trackCustomEvent('signup_click', {
    source: source,
    timestamp: new Date().toISOString(),
  });
  console.log('📊 Signup click tracked from:', source);
};

/**
 * Track signup started (when user enters OTP screen or signup form)
 * @param method - Signup method (e.g., 'email', 'phone', 'google')
 */
export const trackSignupStarted = (method: string): void => {
  trackCustomEvent('signup_started', {
    method: method,
    timestamp: new Date().toISOString(),
  });
  console.log('📊 Signup started with method:', method);
};

/**
 * Track signup completed (when OTP is verified and account created)
 * @param method - Signup method (e.g., 'email', 'phone', 'google')
 * @param userId - Optional user ID
 */
export const trackSignupCompleted = (method: string, userId?: string): void => {
  trackCustomEvent('signup_completed', {
    method: method,
    user_id: userId,
    timestamp: new Date().toISOString(),
  });
  console.log('✅ Signup completed with method:', method);
};

/**
 * Track profile creation completed
 * This is the final step after signup
 * @param authProvider - Auth provider (e.g., 'email', 'phone', 'google', 'facebook')
 * @param userId - Optional user ID
 */
export const trackProfileCreated = (authProvider: string, userId?: string): void => {
  // Main profile creation event
  trackCustomEvent('profile_created', {
    auth_provider: authProvider,
    user_id: userId,
    timestamp: new Date().toISOString(),
  });
  
  // Also send as conversion event for Google Ads
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'conversion_event_profile_creation', {
      auth_provider: authProvider,
      user_id: userId,
    });
  }
  
  console.log('✅ Profile created with auth provider:', authProvider);
};

/**
 * Track user login
 * @param method - Login method (e.g., 'email', 'phone', 'google')
 * @param userId - Optional user ID
 */
export const trackLogin = (method: string, userId?: string): void => {
  trackCustomEvent('login', {
    method: method,
    user_id: userId,
    timestamp: new Date().toISOString(),
  });
  console.log('✅ Login tracked with method:', method);
};

/**
 * Track waitlist signup
 * @param email - User's email address
 */
export const trackWaitlistSignup = (email: string): void => {
  trackCustomEvent('waitlist_signup', {
    email: email,
    timestamp: new Date().toISOString(),
  });
  console.log('✅ Waitlist signup tracked for:', email);
};

/**
 * Track scroll depth
 * @param depth - Scroll depth percentage (e.g., 25, 50, 75, 100)
 */
export const trackScrollDepth = (depth: number): void => {
  trackCustomEvent('scroll_depth', {
    depth: depth,
    page: typeof window !== 'undefined' ? window.location.pathname : '',
  });
  console.log(`📊 Scroll depth tracked: ${depth}%`);
};
